import { Dialog, EmptyState, Icon, Icons, Intent, List, ListItem, Spinner } from "construct-ui";
import codeMasterModel from "../../models/common/code-master.model";
import siteModel from "../../models/site/site.model";
import ViewBase from "../../views/view-base.view";
import LocationTreeCheckboxComponent from "../location/location-tree-checkbox.component";
import MultiSelectComponent from "../multi-select/multi-select.component";
import QueryListComponent from "../query-list/query-list.component";
import SelectUserComponent from "../user/select-user.component";
import SelectSiteGroupComponent from "./select-site-group.component";


let uniqueInstance = 1;

/**
 * Select Site Search Input Dialog
 * 
 * Site Search Dialog with filters to search for sites.
 * 
 * Supports multiple or single inputs
 * 
 * @author Darrus Goh 
 */
export default function SelectSiteSearchInputDialog() {
    ViewBase.call(this);

    let UID = uniqueInstance++;

    const ID_SEARCH_RESULTS = `SelectSiteSearchDialog-SearchResults${UID}`;

    const DEFAULT_ONCLOSE = () => { console.log('SelectSiteSearchInputDialog: onClose()') };
    const DEFAULT_ONSELECT = (selectedItemsItem) => { console.log('SelectSiteSearchInputDialog: onSelect(selectedItems | selectedItem)'); };
    const RESULT_LIST_HEIGHT = '300px';
    const DEFAULT_TOTAL_SITES = 0;

    const DEFAULT_FILTERS = {
        siteIdOrName: '',
        siteGroups: [],
        siteTypes: [],
        locations: [],
        promoters: [],
        siteStatuses: [{ code: '0001', name: 'Active' }]
    };

    const DEFAULT_PAGINATION = {
        pageIndex: 1,
        pageSize: 100
    };

    let filters = structuredClone(DEFAULT_FILTERS);
    let pagination = structuredClone(DEFAULT_PAGINATION);

    let totalSites = DEFAULT_TOTAL_SITES;
    let sites = [];

    let siteStatuses = [];
    let siteTypes = [];

    let isSearching = false;

    const onSearch = (e) => {
        e.preventDefault();

        pagination = structuredClone(DEFAULT_PAGINATION);
        return getSites()
            .then(() => {
                document.getElementById(ID_SEARCH_RESULTS).scrollIntoView({ behavior: 'auto', block: 'end' });
            });
    }

    const onInfiniteScroll = () => {
        if (!isSearching && sites.length != totalSites) {
            pagination.pageIndex++;
            getSites(false);
            m.redraw();
        }
    }

    const getSites = (refresh = true) => {
        isSearching = true;
        return siteModel.getSites(getRequestBody(filters, pagination))
            .then((result) => {
                if (refresh) {
                    sites = generateSiteResults(result.data);
                    totalSites = result.total;
                } else {
                    sites = sites.concat(generateSiteResults(result.data));
                }
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                isSearching = false;
            });
    }

    const generateSiteResults = (list) => {
        const result = structuredClone(list);
        result.forEach(site => { site.siteName = `${site.siteName} [${site.siteId}]` })
        return result;
    }

    const getRequestBody = (filters, pagination) => {
        return Object.assign({
            siteIdOrName: filters.siteIdOrName,
            siteGroups: filters.siteGroups?.map(siteGroup => siteGroup.code) ?? [],
            siteTypes: filters.siteTypes?.map(siteType => siteType.code) ?? [],
            locations: filters.locations ?? [],
            promoters: filters.promoters?.map(user => user.userID) ?? [],
            siteStatuses: filters.siteStatuses?.map(status => status.code) ?? []
        },
            pagination
        );
    }

    const getSiteStatus = () => {
        return codeMasterModel.getByCategoryV2({ category: 'SiteStatus' })
            .then(result => {
                siteStatuses = result;
            })
            .catch((error) => {
                this.handleError(error);
            })
    }

    const getSiteTypes = () => {
        return codeMasterModel.getByCategoryV2({ category: 'SiteType' })
            .then(result => {
                siteTypes = result;
            })
            .catch((error) => {
                this.handleError(error);
            });
    }

    const onDeselect = (selectedItems, onSelect, deselectItem) => {
        let result = structuredClone(selectedItems);
        const index = result.findIndex((element) => deselectItem.code === element.code);
        if (index >= 0) {
            result.splice(index, 1);
        }
        onSelect(result);
    }

    const reset = () => {
        clearFilters();
        pagination = structuredClone(DEFAULT_PAGINATION);
        sites = [];
    }

    const clearFilters = () => {
        filters = structuredClone(DEFAULT_FILTERS);
        totalSites = DEFAULT_TOTAL_SITES;
    }

    const searchViewSingle = (selectedItem, onSelect) => {
        return [
            m('.card.h-100',
                m('.card-body.h-100',
                    sites.length > 0
                        ? m(QueryListComponent, {
                            items: sites,
                            itemKey: 'siteId',
                            itemValue: 'siteName',
                            selectedItem: selectedItem,
                            onSelect: onSelect,
                            onInfiniteScroll: onInfiniteScroll
                        })
                        : m(EmptyState, {
                            class: 'placeholder-text-color',
                            icon: Icons.SEARCH,
                            content: this.getText("LT00000317", "No results found"),
                            fill: true
                        }),
                    m(Spinner, { fill: true, background: true, active: isSearching })
                )
            ),
            m('.hstack.gap-2.justify-content-end',
                m('small', `${sites.length} / ${totalSites} record(s)`)
            )
        ];
    };

    const searchViewMultiple = (selectedItems, onSelect) => {
        return m('.row.g-3.h-100',
            m('.col-lg-6.h-100.order-lg-2',
                m('.card.h-100',
                    m('.card-body.h-100',
                        selectedItems.length > 0
                            ? m(List, { class: 'h-100', interactive: true },
                                selectedItems.map(item => m(ListItem, {
                                    label: item.siteName,
                                    contentRight: m(Icon, {
                                        name: Icons.X,
                                        intent: Intent.NEGATIVE,
                                        onclick: () => { onDeselect(selectedItems, onSelect, item) }
                                    }),
                                }))
                            )
                            : m(EmptyState, {
                                class: 'placeholder-text-color',
                                icon: Icons.MAP_PIN,
                                content: this.getText('EM00000375', 'No Sites selected'),
                                fill: true
                            })
                    )
                ),
                m('.hstack.gap-2.justify-content-end',
                    m('a.small.cursor-pointer', { onclick: () => { onSelect([]) } }, this.getText('LT00000545', 'Clear selections')),
                    m('small', `${selectedItems.length} record(s)`)
                )
            ),
            m(`div#${ID_SEARCH_RESULTS}.col-lg-6.h-100.order-lg-1`,
                m('.card.h-100',
                    m('.card-body.h-100',
                        (sites.length > 0 || isSearching)
                            ? [
                                m(QueryListComponent, {
                                    items: sites,
                                    itemKey: 'siteId',
                                    itemValue: 'siteName',
                                    selectedItems: selectedItems,
                                    multiple: true,
                                    onSelect: onSelect,
                                    onInfiniteScroll: onInfiniteScroll
                                }),
                                m(Spinner, { fill: true, background: true, active: isSearching })
                            ]
                            : m(EmptyState, {
                                class: 'placeholder-text-color',
                                icon: Icons.SEARCH,
                                content: this.getText("LT00000317", "No results found"),
                                fill: true
                            })
                    )
                ),
                m('.hstack.gap-2.justify-content-end',
                    m('small', `${sites.length} / ${totalSites} record(s)`)
                )
            )
        )
    };

    const searchViewFunctions = [searchViewSingle, searchViewMultiple];

    return {
        oninit: () => {
            getSiteStatus();
            getSiteTypes();
        },
        view: ({ attrs: {
            selectedItems = [],
            selectedItem = null,
            multiple = false,
            isOpen = false,
            onSelect = DEFAULT_ONSELECT,
            onClose = DEFAULT_ONCLOSE } }) => {
            let selected = multiple ? selectedItems : selectedItem;

            return m(Dialog, {
                class: 'cui-dialog-xl',
                isOpen,
                closeOnOutsideClick: false,
                closeOnEscapeKey: false,
                onClose: onClose,
                onClosed: () => {
                    reset();
                },
                title: this.getText('LT00000810', 'Site Search'),
                content: [
                    m('ul.nav.nav-tabs',
                        m('li.nav-item',
                            m('a.nav-link.active', {
                                id: 'all-sites-tab',
                                'data-bs-target': '#all-sites-pane',
                                type: 'button',
                                role: 'tab',
                                'aria-controls': 'all-sites-pane',
                                'aria-selected': true
                            },
                                this.getText('LT00000811', 'All Sites')),
                        )
                    ),
                    m('.tab-content',
                        m('.tab-pane.show.active', { id: 'all-sites-pane', role: 'tabpanel', 'aria-labelledby': 'all-sites-tab', tabindex: 0 },
                            m('.vstack.gap-2',
                                m('.hstack.justify-content-end',
                                    m('.d-block.d-md-block.d-lg-none',
                                        m('a.btn.btn-primary[data-bs-toggle="collapse"][href="#search-section"][aria-controls="search-section"][aria-expanded="false"]',
                                            m("i.bi.bi-filter"),
                                            this.getText("LT00000232", "Filter")
                                        )
                                    )
                                ),
                                m(".collapse.show#search-section",
                                    m('.card',
                                        m('.card-body',
                                            m('form.row.g-2', { onsubmit: onSearch },
                                                m('.col-lg-4',
                                                    m('label.form-label', `${this.getText('LT00000144', 'Site Name')} | ${this.getText('LT00000809', 'Site ID')}`),
                                                    m('input.form-control', {
                                                        type: 'text',
                                                        maxlength: 255,
                                                        value: filters?.siteIdOrName,
                                                        placeholder: `${this.getText('LT00000144', 'Site Name')}, ${this.getText('LT00000809', 'Site ID')}`,
                                                        onchange: (element) => {
                                                            filters.siteIdOrName = element.target.value;
                                                        }
                                                    })
                                                ),
                                                m('.col-lg-4',
                                                    m('label.form-label', this.getText('LT00000072', 'Site Group')),
                                                    m(SelectSiteGroupComponent, {
                                                        selectedItems: filters?.siteGroups,
                                                        onChange: (selectedSiteGroups) => {
                                                            filters.siteGroups = selectedSiteGroups;
                                                        }
                                                    })
                                                ),
                                                m('.col-lg-4',
                                                    m('label.form-label', this.getText('LT00000808', 'Site Type')),
                                                    m(MultiSelectComponent, {
                                                        items: siteTypes,
                                                        selectedItems: filters?.siteTypes,
                                                        filterable: false,
                                                        onSelect: (selectedSiteTypes) => {
                                                            filters.siteTypes = selectedSiteTypes;
                                                        }
                                                    })
                                                ),
                                                m('.col-lg-4',
                                                    m('label.form-label', this.getText('LT00000557', 'Location')),
                                                    m(LocationTreeCheckboxComponent, {
                                                        selectedCodes: filters?.locations,
                                                        onChange: (selectedLocations) => {
                                                            filters.locations = selectedLocations;
                                                        }
                                                    })
                                                ),
                                                m('.col-lg-4',
                                                    m('label.form-label', this.getText('LT00000561', 'Promoter')),
                                                    m(SelectUserComponent, {
                                                        selectedUserList: filters?.promoters,
                                                        onSelect: (selectedUsers) => {
                                                            filters.promoters = selectedUsers;
                                                        }
                                                    })
                                                ),
                                                m('.col-lg-4',
                                                    m('label.form-label', this.getText('LT00000807', 'Site Status')),
                                                    m(MultiSelectComponent, {
                                                        items: siteStatuses,
                                                        selectedItems: filters?.siteStatuses,
                                                        filterable: false,
                                                        onSelect: (selectedSiteStatuses) => {
                                                            filters.siteStatuses = selectedSiteStatuses;
                                                        }
                                                    })
                                                ),
                                                m('.col-12',
                                                    m('.d-flex.gap-2.justify-content-end',
                                                        m('button.btn.btn-primary', this.getText("LT00000001", "Search")),
                                                        m('button.btn.btn-white', { type: 'button', onclick: clearFilters }, this.getText("LT00000031", "Clear"))
                                                    )
                                                )
                                            ),
                                        )
                                    )
                                ),
                                m('div', { style: { height: RESULT_LIST_HEIGHT } },
                                    searchViewFunctions[+multiple](selected, onSelect)
                                )
                            )
                        )
                    ),
                    this.getMessageComponent()
                ],
                footer: m('.hstack.justify-content-end',
                    m('button.btn.btn-white', { onclick: onClose }, this.getText('LT00000021', 'Close'))
                )
            })
        }
    }
}